/**
 * Invite Peter Lang User Modal
 *
 * A modal dialog for inviting users from Azure Active Directory.
 * Provides typeahead search for AD users, auto-fills email/name,
 * and allows role selection before sending the invitation.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { apiRequest } from '../../lib/api';
import { RbacRoleWithPermissions } from '../../lib/types';

interface EntraUserSearchResult {
  id: string;
  displayName: string;
  userPrincipalName: string;
}

interface InvitePLUserModalProps {
  roles: RbacRoleWithPermissions[];
  onClose: () => void;
  onCreated: () => void;
}

export function InvitePLUserModal({ roles, onClose, onCreated }: InvitePLUserModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<EntraUserSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [suppressSearch, setSuppressSearch] = useState(false);

  const [selectedUser, setSelectedUser] = useState<EntraUserSearchResult | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Group roles by category
  const rolesByCategory = useMemo(() => {
    const grouped: Record<string, RbacRoleWithPermissions[]> = {};
    for (const role of roles) {
      const category = role.category || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(role);
    }
    // Sort categories: Platform first, then alphabetically, Other last
    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      if (a === 'Platform') return -1;
      if (b === 'Platform') return 1;
      if (a === 'Other') return 1;
      if (b === 'Other') return -1;
      return a.localeCompare(b);
    });
    return { grouped, sortedCategories };
  }, [roles]);

  // Entra user search with debounce
  useEffect(() => {
    if (suppressSearch) {
      setSuppressSearch(false);
      return;
    }

    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchError(null);
      return;
    }

    const handle = setTimeout(async () => {
      try {
        setIsSearching(true);
        setSearchError(null);
        const params = new URLSearchParams({ q, limit: '10' });
        const response = await apiRequest('GET', `/api/entra/users/search?${params.toString()}`);
        if (!response.ok) {
          const err = await response.json().catch(() => null);
          throw new Error(err?.message || `Search failed (${response.status})`);
        }
        const data = await response.json();
        setSearchResults(data.users || []);
      } catch (err: any) {
        setSearchResults([]);
        setSearchError(err?.message || 'Failed to search users');
      } finally {
        setIsSearching(false);
      }
    }, 250);

    return () => clearTimeout(handle);
  }, [searchQuery]);

  const handleSelectUser = (user: EntraUserSearchResult) => {
    setSelectedUser(user);
    setSuppressSearch(true);
    setSearchQuery(`${user.displayName} (${user.userPrincipalName})`);
    setSearchResults([]);
    setMessage(null);
  };

  const handleClearSelection = () => {
    setSelectedUser(null);
    setSearchQuery('');
    setSearchResults([]);
    setMessage(null);
  };

  const toggleRole = (roleId: string) => {
    setSelectedRoles(prev =>
      prev.includes(roleId)
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUser) {
      setMessage({ type: 'error', text: 'Please select a user from the directory' });
      return;
    }

    if (selectedRoles.length === 0) {
      setMessage({ type: 'error', text: 'At least one role is required' });
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await apiRequest('POST', '/api/invitations', {
        email: selectedUser.userPrincipalName,
        displayName: selectedUser.displayName || undefined,
        roleIds: selectedRoles,
        expirationHours: 168, // 7 days
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `Invitation sent to ${selectedUser.displayName}` });
        setTimeout(() => {
          onCreated();
          onClose();
        }, 1500);
      } else if (response.status === 409) {
        const error = await response.json();
        if (error.code === 'INVITATION_EXISTS' && error.existingInvitation) {
          const createdDate = new Date(error.existingInvitation.createdAt).toLocaleDateString();
          if (confirm(`An invitation was already sent to ${selectedUser.displayName} on ${createdDate}. Do you want to revoke it and send a new one?`)) {
            // Revoke the existing invitation
            const revokeResponse = await apiRequest('POST', `/api/invitations/${error.existingInvitation.invitationId}/revoke`);
            if (revokeResponse.ok) {
              // Retry creating the invitation
              const retryResponse = await apiRequest('POST', '/api/invitations', {
                email: selectedUser.userPrincipalName,
                displayName: selectedUser.displayName || undefined,
                roleIds: selectedRoles,
                expirationHours: 168,
              });
              if (retryResponse.ok) {
                setMessage({ type: 'success', text: `Previous invitation revoked. New invitation sent to ${selectedUser.displayName}` });
                setTimeout(() => {
                  onCreated();
                  onClose();
                }, 1500);
              } else {
                const retryError = await retryResponse.json();
                setMessage({ type: 'error', text: retryError.message || 'Failed to send new invitation' });
              }
            } else {
              setMessage({ type: 'error', text: 'Failed to revoke existing invitation' });
            }
          } else {
            setMessage({ type: 'error', text: 'Invitation not sent - existing invitation kept' });
          }
        } else {
          setMessage({ type: 'error', text: error.message || 'Invitation already exists' });
        }
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.message || 'Failed to send invitation' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Failed to send invitation' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg p-6 max-w-2xl w-full shadow-xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4 text-foreground">Invite Peter Lang User</h2>

        {message && (
          <div className={`mb-4 p-3 rounded-md text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Search Input */}
          <div>
            <label htmlFor="user-search" className="block text-sm font-medium text-foreground mb-1">
              Search Directory <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="user-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md shadow-sm bg-background text-foreground focus:ring-primary focus:border-primary"
                placeholder="Search by name or email..."
                disabled={isSubmitting}
              />
              {(isSearching || searchResults.length > 0) && (
                <div className="absolute z-10 mt-1 w-full bg-card border border-border rounded-md shadow-lg max-h-64 overflow-auto">
                  {isSearching && (
                    <div className="p-3 text-sm text-muted-foreground">Searching...</div>
                  )}
                  {!isSearching && searchResults.map(user => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleSelectUser(user)}
                      className="w-full text-left px-3 py-2 hover:bg-muted"
                    >
                      <div className="text-sm font-medium text-foreground">{user.displayName}</div>
                      <div className="text-xs text-muted-foreground">{user.userPrincipalName}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {searchError && (
              <div className="mt-1 text-sm text-red-600">{searchError}</div>
            )}
            <p className="mt-1 text-xs text-muted-foreground">Type at least 2 characters to search</p>
          </div>

          {/* Selected User Display */}
          {selectedUser && (
            <div className="bg-primary/10 border border-primary/30 rounded-md p-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-medium text-foreground">{selectedUser.displayName}</div>
                  <div className="text-xs text-muted-foreground">{selectedUser.userPrincipalName}</div>
                </div>
                <button
                  type="button"
                  onClick={handleClearSelection}
                  disabled={isSubmitting}
                  className="text-primary hover:text-primary/80 text-sm"
                >
                  Change
                </button>
              </div>
            </div>
          )}

          {/* Role Selection - Grouped by Category */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Roles <span className="text-red-500">*</span>
            </label>
            <div className="border border-border rounded-md overflow-hidden">
              {rolesByCategory.sortedCategories.map((category, categoryIndex) => (
                <div key={category} className={categoryIndex > 0 ? 'border-t border-border' : ''}>
                  <div className="bg-muted px-3 py-2 border-b border-border">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{category}</span>
                  </div>
                  <div className="p-2 grid grid-cols-2 gap-1">
                    {rolesByCategory.grouped[category].map(role => (
                      <label
                        key={role.roleId}
                        className="flex items-start cursor-pointer hover:bg-muted p-2 rounded"
                        title={role.description || undefined}
                      >
                        <input
                          type="checkbox"
                          checked={selectedRoles.includes(role.roleId)}
                          onChange={() => toggleRole(role.roleId)}
                          className="mt-0.5 mr-2 h-4 w-4 text-primary focus:ring-primary border-border rounded"
                          disabled={isSubmitting}
                        />
                        <div>
                          <span className="text-sm text-foreground">{role.name}</span>
                          {role.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">{role.description}</p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-muted-foreground bg-muted p-2 rounded">
            Invitation expires in 7 days. User will receive an email with a secure link to accept.
          </p>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !selectedUser}
              className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
